import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { vehiclesApi, brandsApi, modelsApi, enumsApi, imagesApi, pdfsApi } from '../lib/api'
import { useLanguage } from '../contexts/LanguageContext'
import { Upload, X, Image as Trash2, Search, Loader } from 'lucide-react'
import Schema from '../components/Schema'

const imageTypes = [
  { value: 'main', label: 'Thumbnail' },
  { value: 'thumbnail', label: 'Cover' },
  // { value: 'option', label: 'Option' },
  { value: 'cover', label: 'Main' }
]

const address = {
  az: 'Nö. 875, 8-ci mərtəbə, Cungbong-dero 490, So-qu, İnçon',
  en: 'No.875, 8th Floor, 490 Jungbong-daero, Seo-gu, Incheon',
  ru: 'Но. 875, Этаж 8, 490 Чонбонг-даэро, Со-гу, Инчхон',
  ko: '인천광역시 서구 중봉대로 490, 8층, 875호',
  kk: 'Но. 875, Этаж 8, 490 Чонбонг-даэро, Со-гу, Инчхон',
  ky: 'Но. 875, Этаж 8, 490 Чонбонг-даэро, Со-гу, Инчхон'
}

export default function VehicleForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const [selectedImages, setSelectedImages] = useState([])
  const [optionsCopy, setOptionsCopy] = useState([])
  const [modelValue, setModelValue] = useState('')
  const [selectedImageCount, setSelectedImageCount] = useState(0)
  const [uploadingImages, setUploadingImages] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(null)
  const [showImageModal, setShowImageModal] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState([])
  const [delayedData, setDelayedData] = useState({
    isEdit: false,
    manufacturer_id: null,
    model_group_id: null,
    model_group_name: null,
    fuel_type_id: null,
    color_id: null,
    transmission_id: null,
    body_type_id: null,
    delivery_status_id: null,
    sale_status_id: null,
    additional_status: null,
    seller_note: null,
    address,
    youtube_urls: null,
    total_accidents_cost: null
  })
  const [has360View, setHas360View] = useState(false)
  const [images360, setImages360] = useState([])
  const [uploading360, setUploading360] = useState(false)

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const sixMonthsLater = new Date();
  sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);

  const { register, handleSubmit, formState: { errors }, setValue, watch, getValues } = useForm({
    defaultValues: {
      model_group_id: '',
      manufacturer_id: '',
      year: '',
      delivery_status_id: '',
      sale_status_id: '',
      status: 'active',
      additional_status: '',
      youtube_urls: {},
      car_damage_report: {},
      fuel_type_id: '',
      engine: '',
      trim_id: '',
      mileage: '',
      price: '',
      color_id: '',
      transmission_id: '',
      body_type_id: '',
      seat_count: '',
      number_of_doors: '',
      description_az: '',
      description_ru: '',
      description_en: '',
      description_ko: '',
      description_kk: '',
      description_ky: '',
      fem_report_url: '',
      sale: true,
      featured: false,
      // Main Data Section
      seller_note: '',
      seller: 'Carify LLC',
      phone_number: '+82 (10) 7216 7665',
      address: '',
      // History Section
      total_accidents: '0',
      plate_changes: '1',
      owner_accidents: '0',
      flood_damage_count: '',
      owner_changes: '1',
      total_loss_count: '',
      previous_usage: 'personal',
      has_rental_use: false,
      // Technical Inspection
      vin: '',
      passed: '',
      inspector_notes: '',
      odometer_status: 'original',
      vin_status: 'verified',
      inspection_status: '85',
      inspection_date: yesterday.toISOString().split('T')[0],
      has_repairs: false,
      report_pdf: null,
      report_pdf_url: null,
      is_360: false,
      // Warranty Section
      warranty_expires: sixMonthsLater.toISOString().split('T')[0],
      warranty_mileage: '100',
      warranty_type: '',
      isWarranted: false,
      duration_months: '48',
      warranty_notes: '',
      image_360: []
    }
  })

  const selectedBrandId = watch('manufacturer_id')

  // Fetch all required data
  const { data: brandsData, isLoading: loading_brandsData } = useQuery(
    'brands-for-vehicle',
    () => brandsApi.getAll({ limit: 100 }),
    { select: (data) => data.data?.results || [] }
  )

  const { data: modelsData, isLoading: isModelLoading } = useQuery(
    ['models-for-vehicle', selectedBrandId],
    () => selectedBrandId ? modelsApi.getByManufacturer(selectedBrandId) : Promise.resolve({ data: { results: [] } }),
    {
      select: (data) => data.data?.results || [],
      enabled: !!selectedBrandId,
      onSuccess: (data) => {
        if (isEdit) {
          setModelValue(delayedData.model_group_id)
        }
      }
    }
  )

  const { data: fuelTypesData, isLoading: loading_fuelTypesData } = useQuery(
    'fuel-types-for-vehicle',
    () => enumsApi.fuelTypes.getAll({ limit: 100 }),
    {
      select: (data) => {
        return data?.data?.data?.results || []
      }
    }
  )

  const { data: colorsData, isLoading: loading_colorsData } = useQuery(
    'colors-for-vehicle',
    () => enumsApi.colors.getAll({ limit: 100 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  const { data: transmissionsData, isLoading: loading_transmissionsData } = useQuery(
    'transmissions-for-vehicle',
    () => enumsApi.transmissions.getAll({ limit: 100 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  const { data: bodyTypesData, isLoading: loading_bodyTypesData } = useQuery(
    'body-types-for-vehicle',
    () => enumsApi.bodyTypes.getAll({ limit: 100 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  const { data: deliveryStatusesData, isLoading: loading_deliveryStatusesData } = useQuery(
    'delivery-statuses-for-vehicle',
    () => enumsApi.deliveryStatuses.getAll({ limit: 100 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  const { data: saleStatusesData, isLoading: loading_saleStatusesData } = useQuery(
    'sale-statuses-for-vehicle',
    () => enumsApi.saleStatuses.getAll({ limit: 100 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  const { data: optionsData, isLoading: loading_optionsData } = useQuery(
    'options-for-vehicle',
    () => enumsApi.options.getAll({ limit: 200 }),
    {
      select: (data) => data.data?.data?.results || [],
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setOptionsCopy(data)
          if (!isEdit) {
            const activeOptions = data.filter(option => option.is_active)
            setSelectedOptions(activeOptions.map(option => option.id))
          }
        }
      }
    }
  )

  const { data: warrantyTypesData, isLoading: loading_warrantyTypesData } = useQuery(
    'warranty-types-for-vehicle',
    () => enumsApi.warrantyTypes.getAll({ limit: 100 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  const { data: vehicleTypesData } = useQuery(
    'vehicle-types-for-vehicle',
    () => enumsApi.vehicleTypes.getAll({ limit: 100 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  const { data: trimsData } = useQuery(
    'trims-for-vehicle',
    () => enumsApi.trims.getAll({ limit: 200 }),
    { select: (data) => data?.data?.data?.results || [] }
  )

  // Fetch vehicle data for editing
  const { data: vehicleData, isLoading: isLoadingVehicle } = useQuery(
    ['vehicle', id],
    () => vehiclesApi.getById(id),
    {
      enabled: isEdit,
      select: (data) => data.data,
      onSuccess: (data) => {
        if (data) {
          setDelayedData({
            isEdit: true,
            manufacturer_id: data?.manufacturer_id,
            model_group_id: data?.model?.model_group?.id,
            model_group_name: data?.model?.model_group?.name,
            fuel_type_id: data?.fuel_type_id,
            color_id: data?.color_id,
            transmission_id: data?.transmission_id,
            body_type_id: data?.body_type_id,
            delivery_status_id: data?.delivery_status_id,
            sale_status_id: data?.sale_status_id,
            additional_status: data?.additional_status,
            seller_note: data?.main_data?.seller_note,
            address: data?.main_data?.address,
            youtube_urls: data?.youtube_urls,
            total_accidents_cost: data?.total_accidents_cost
          })

          setValue('model_group_id', data?.model?.model_group?.id)
          setValue('manufacturer_id', data.manufacturer_id || '')
          setValue('year', data.year)
          setValue('delivery_status_id', data.delivery_status_id)
          setValue('sale_status_id', data.sale_status_id || '')
          setValue('status', data.status)
          setValue('additional_status', data.additional_status)

          //youtube video links
          setValue('youtube_urls', data.youtube_urls)
          setValue('youtube_urls_az', data.youtube_urls?.az)
          setValue('youtube_urls_en', data.youtube_urls?.en)
          setValue('youtube_urls_ru', data.youtube_urls?.ru)
          setValue('youtube_urls_ko', data.youtube_urls?.ko)
          setValue('youtube_urls_kk', data.youtube_urls?.kk || data.youtube_urls?.ru)
          setValue('youtube_urls_ky', data.youtube_urls?.ky || data.youtube_urls?.ru)

          setValue('fuel_type_id', data.fuel_type_id)
          setValue('engine', data.engine)
          setValue('trim_id', data.trim_id || '')
          setValue('mileage', data.mileage)
          setValue('price', data.price)
          setValue('color_id', data.color_id)
          setValue('transmission_id', data.transmission_id)
          setValue('body_type_id', data.body_type_id)
          setValue('vehicle_type_id', data.vehicle_type_id || '')
          setValue('seat_count', data.seat_count)
          setValue('number_of_doors', data.number_of_doors || '')
          setValue('description_az', data.description_az || '')
          setValue('description_ru', data.description_ru || '')
          setValue('description_en', data.description_en || '')
          setValue('description_ko', data.description_ko || '')
          setValue('description_kk', data.description_kk || '')
          setValue('description_ky', data.description_ky || '')
          setValue('fem_report_url', data.fem_report_url || '')
          setValue('sale', data.sale)
          setValue('featured', data.featured === 1 ? true : false)
          setHas360View(Boolean(data.is_360))
          setImages360(data.image_360 || [])
          setValue('is_360', Boolean(data.is_360))
          setValue('image_360', data.image_360 || [])
          setValue('total_accidents_cost', data.total_accidents_cost)
          setValue('report_pdf', data.technical_inspection.report_pdf || null)

          // Main Data
          if (data.main_data) {
            setValue('seller_note', data.main_data.seller_note || '')
            setValue('seller', data.main_data.seller || 'Carify LLC')
            setValue('phone_number', data.main_data.phone_number || '+82 (10) 7216 7665')
            setValue('address', data.main_data.address || '')
          }

          // History
          if (data.history) {
            setValue('total_accidents', data.history.total_accidents || '0')
            setValue('plate_changes', data.history.plate_changes || '1')
            setValue('owner_accidents', data.history.owner_accidents || '0')
            setValue('flood_damage_count', data.history.flood_damage_count || '')
            setValue('owner_changes', data.history.owner_changes || '1')
            setValue('total_loss_count', data.history.total_loss_count || '')
            setValue('previous_usage', data.history.previous_usage || 'personal')
            setValue('has_rental_use', data.history.has_rental_use || false)
          }

          // Technical Inspection
          if (data.technical_inspection) {
            setValue('vin', data.vin || data.technical_inspection.vin || '')
            setValue('passed', data.technical_inspection.passed || '')
            setValue('inspector_notes', data.technical_inspection.inspector_notes || '')
            setValue('odometer_status', data.technical_inspection.odometer_status || '')
            setValue('vin_status', data.technical_inspection.vin_status || '')
            setValue('inspection_status', data.technical_inspection.status || '')
            setValue('inspection_date', data.technical_inspection.inspection_date || '')
            setValue('has_repairs', data.technical_inspection.has_repairs || false);
            setValue('car_damage_report', data.technical_inspection.car_damage_report)
            setValue('car_damage_report_id', data.technical_inspection.car_damage_report?.id)
            setValue('report_pdf_url', data.technical_inspection.report_pdf || null)
          }

          // Warranty
          if (data.warranty) {
            setValue('warranty_expires', data.warranty.warranty_expires || '')
            setValue('warranty_mileage', data.warranty.warranty_mileage || '')
            setValue('warranty_type', data.warranty.warranty_type || '')
            setValue('isWarranted', data.warranty.isWarranted || false)
            setValue('duration_months', data.warranty.duration_months || '')
            setValue('warranty_notes', data.warranty.notes || '')
          }

          // Options
          if (data.options && data.options.length > 0) {
            setSelectedOptions(data.options.map(opt => opt.id))
          }

          // Images/Photos
          if (data.photos && data.photos.length > 0) {
            const loadedImages = data.photos.map(photo => ({
              id: photo.id,
              path: photo.image_url,
              type: photo.type,
              isExisting: true
            }))
            setSelectedImages(loadedImages)
          }
        }
      }
    }
  )

  const createMutation = useMutation(
    (data) => vehiclesApi.create(data),
    {
      onSuccess: () => {
        toast.success('Vehicle created successfully')
        queryClient.invalidateQueries(['vehicles'])
        navigate('/vehicles')
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          return error.response?.data?.errors.forEach(error => {
            toast.error(`${error.field}: ${error.message}`)
          })
        } else {
          toast.error(error.response?.data?.message || 'Failed to create vehicle')
        }
      }
    }
  )

  const updateMutation = useMutation(
    (data) => vehiclesApi.update(id, data),
    {
      onSuccess: () => {
        toast.success('Vehicle updated successfully')
        queryClient.invalidateQueries(['vehicles'])
        navigate('/vehicles')
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          return error.response?.data?.errors.forEach(error => {
            toast.error(`${error.field}: ${error.message}`)
          })
        }
        toast.error(error.response?.data?.message || 'Failed to update vehicle')
      }
    }
  )
  const handleImageUpload = async (files) => {
    setSelectedImageCount(prev => prev + files.length)
    setUploadingImages(true)
    try {
      const formData = new FormData()
      Array.from(files).forEach(file => {
        formData.append('images', file)
      })

      const response = await imagesApi.upload(formData)
      const uploadedImages = response.data.data.map((img, index) => {
        return {
          id: img.id,
          path: img.path?.split('digitaloceanspaces.com')[1]?.trim(),
          type: 'main',
          file: files[index]
        }
      })

      setSelectedImages(prev => [...prev, ...uploadedImages])
    } catch (error) {
      toast.error('Failed to upload images')
      console.error('Image upload error:', error)
    } finally {
      setUploadingImages(false)
    }
  }

  const handle360Upload = async (files) => {
    if (!files || files.length === 0) return
    setUploading360(true)
    try {
      const formData = new FormData()
      Array.from(files).forEach(file => {
        formData.append('images360', file)
      })
      const response = await imagesApi.upload360(formData)
      const uploaded = response.data.data || []
      setImages360(prev => {
        const next = [...prev, ...uploaded]
        setValue('image_360', next)
        return next
      })
      toast.success('360° image uploaded successfully')
    } catch (error) {
      console.error('360 upload error:', error)
      toast.error('Failed to upload 360° image')
    } finally {
      setUploading360(false)
    }
  }

  const remove360Image = (index) => {
    setImages360(prev => {
      const next = prev.filter((_, i) => i !== index)
      setValue('image_360', next)
      return next
    })
  }

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index))
  }

  const updateImageType = async (index, type) => {
    const image = selectedImages[index]

    // Update state immediately
    setSelectedImages(prev => prev.map((img, i) =>
      i === index ? { ...img, type } : img
    ))

    // If editing an existing vehicle and this is an existing image, update in database
    if (isEdit && image.isExisting && id) {
      try {
        await vehiclesApi.updateImageType(id, image.id, type)
        toast.success('Image type updated successfully')
      } catch (error) {
        console.error('Error updating image type:', error)
        toast.error('Failed to update image type in database')
      }
    }
  }

  const searchOption = (value) => {
    const search = value.trim().toLocaleLowerCase('tr-AZ')

    if (search) {
      setOptionsCopy(
        optionsData.filter(item => {
          const names = [
            item.name_az,
            item.name_ru,
            item.name_en,
            item.name_ko,
            item.name
          ]

          return names.some(name =>
            name?.toLocaleLowerCase('tr-AZ').includes(search)
          )
        })
      )
    } else setOptionsCopy(optionsData)
  }

  const openImageModal = (index) => {
    setSelectedImageIndex(index)
    setShowImageModal(true)
  }

  const closeImageModal = () => {
    setShowImageModal(false)
    setSelectedImageIndex(null)
  }

  const deleteImageFromModal = () => {
    if (selectedImageIndex !== null) {
      removeImage(selectedImageIndex)
      closeImageModal()
    }
  }

  const toggleOption = (optionId) => {
    setSelectedOptions(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId)
      } else {
        return [...prev, optionId]
      }
    })
  }

  const handleReportPdfUpload = async (e) => {
    const file = e.target.files[0]
    try {
      const formData = new FormData()
      formData.append('report_pdf', file)
      const response = await pdfsApi.upload(formData)
      toast.success('Report PDF uploaded successfully')
      setValue('report_pdf_url', response.data.data.path)
      setValue('report_pdf', response.data.data.id.toString())
    } catch (error) {
      console.error('Report PDF upload error:', error)
      toast.error('Failed to upload report PDF')
    }
  }


  const onSubmit = (data) => {
    // Prepare the data with proper structure
    const deletedFields = ["additional_status_az", "additional_status_ru", "additional_status_en", "additional_status_ko", "additional_status_kk", "additional_status_ky", "seller_note_az", "seller_note_ru", "seller_note_en", "seller_note_ko", "seller_note_kk", "seller_note_ky", "address_az", "address_ru", "address_en", "address_ko", "address_kk", "address_ky", "youtube_urls_az", "youtube_urls_ru", "youtube_urls_en", "youtube_urls_ko", "youtube_urls_kk", "youtube_urls_ky", "total_accidents", "plate_changes", "owner_accidents", "flood_damage_count", "owner_changes", "total_loss_count", "previous_usage", "has_rental_use", "passed", "inspector_notes", "odometer_status", "vin_status", "inspection_status", "inspection_date", "has_repairs", "warranty_expires", "warranty_mileage", "warranty_type", "isWarranted", "duration_months", "warranty_notes", "warranty_type_id", 'report_pdf_url']

    const normalized360Images = has360View
      ? images360.map(image => ({
        id: image.id,
        path: image.path,
        filename: image.filename
      }))
      : []

    const vehicleData = {
      ...data,
      main_data: {
        seller_note: {
          az: data.seller_note_az,
          ru: data.seller_note_ru,
          en: data.seller_note_en,
          ko: data.seller_note_ko,
          kk: data.seller_note_kk || data.seller_note_ru,
          ky: data.seller_note_ky || data.seller_note_ru
        },
        seller: data.seller,
        phone_number: data.phone_number,
        address: {
          az: data.address_az,
          ru: data.address_ru,
          en: data.address_en,
          ko: data.address_ko,
          kk: data.address_kk || data.address_ru,
          ky: data.address_ky || data.address_ru
        }
      },
      additional_status: {
        az: data.additional_status_az,
        ru: data.additional_status_ru,
        en: data.additional_status_en,
        ko: data.additional_status_ko,
        kk: data.additional_status_kk || data.additional_status_ru,
        ky: data.additional_status_ky || data.additional_status_ru
      },
      history: {
        total_accidents: data.total_accidents,
        plate_changes: data.plate_changes,
        owner_accidents: data.owner_accidents,
        flood_damage_count: data.flood_damage_count,
        owner_changes: data.owner_changes,
        total_loss_count: data.total_loss_count,
        previous_usage: data.previous_usage,
        has_rental_use: data.has_rental_use
      },

      technical_inspection: {
        vin: data.vin,
        passed: data.passed,
        inspector_notes: data.inspector_notes,
        odometer_status: data.odometer_status,
        vin_status: data.vin_status,
        status: data.inspection_status,
        inspection_date: data.inspection_date,
        has_repairs: data.has_repairs,
        report_pdf: data.report_pdf_url
      },
      warranty: {
        warranty_expires: data.warranty_expires,
        warranty_mileage: data.warranty_mileage,
        warranty_type: data.warranty_type,
        isWarranted: data.isWarranted,
        duration_months: data.duration_months,
        notes: data.warranty_notes
      },
      images: selectedImages.map(img => ({
        id: img.id,
        path: img.path,
        type: img.type
      })),
      is_360: has360View,
      image_360: normalized360Images,
      youtube_urls: {
        az: data.youtube_urls?.az?.map(url => url.trim()),
        ru: data.youtube_urls?.ru?.map(url => url.trim()),
        en: data.youtube_urls?.en?.map(url => url.trim()),
        ko: data.youtube_urls?.ko?.map(url => url.trim()),
        kk: (data.youtube_urls?.kk || data.youtube_urls?.ru)?.map(url => url.trim()),
        ky: (data.youtube_urls?.ky || data.youtube_urls?.ru)?.map(url => url.trim())
      },
      sale: Boolean(data.sale),
      options: selectedOptions,
      report_pdf: data.report_pdf
    }

    deletedFields.forEach(field => {
      delete vehicleData[field]
    })

    if (isEdit) {
      updateMutation.mutate(vehicleData)
    } else {
      createMutation.mutate(vehicleData)
    }
  }

  const isLoading = createMutation.isLoading || updateMutation.isLoading

  if (isEdit && isLoadingVehicle) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-96" />
        </div>
        <Card>
          <CardHeader>
            <div className="h-6 bg-gray-200 rounded w-48 animate-pulse" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
                  <div className="h-10 bg-gray-200 rounded w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (
    loading_brandsData || loading_fuelTypesData || loading_colorsData || loading_transmissionsData || loading_bodyTypesData || loading_deliveryStatusesData || loading_saleStatusesData || loading_optionsData || loading_warrantyTypesData
  ) return <Loader />

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Vehicle' : 'Add New Vehicle'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {isEdit ? 'Update vehicle information' : 'Add a new vehicle to the inventory'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Photos Section */}
        <Card>
          <CardHeader>
            <CardTitle>Photos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 ">
              <div className="border-2 relative border-dashed border-gray-300 rounded-lg p-6 ">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Upload vehicle photos
                      </span>
                      <input
                        id="image-upload"
                        type="file"
                        multiple
                        accept="image/*"
                        className=" cursor-pointer absolute top-0 left-0 z-[9999] w-full h-full opacity-0"
                        onChange={(e) => handleImageUpload(e.target.files)}
                        disabled={uploadingImages}
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB each
                    </p>
                  </div>
                </div>
              </div>
              {uploadingImages ? (
                <div className="flex items-center gap-2 flex-wrap">
                  {Array.from({ length: selectedImageCount }).map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="size-[70px] rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 transition-opacity" />
                    </div>
                  ))}
                </div>
              ) :
                selectedImages.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {selectedImages.map((image, index) => (
                      <div key={index} className="relative group size-[70px]">
                        <div
                          className="w-[70px] h-[70px] rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openImageModal(index)}
                        >
                          <img
                            src={image.isExisting ? `${import.meta.env.VITE_IMAGE_URL}${image.path}` : URL.createObjectURL(image.file)}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute -top-1 -right-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeImage(index)
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        {/* Badge showing image type */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[10px] px-1 py-0.5 text-center">
                          {imageTypes.find(item => item.value == image.type).label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              <div className="border-t pt-4 mt-4 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={has360View}
                    onChange={(e) => {
                      setHas360View(e.target.checked)
                      setValue('is_360', e.target.checked)
                      if (!e.target.checked) {
                        setImages360([])
                        setValue('image_360', [])
                      }
                    }}
                  />
                  360° image available
                </label>
                {has360View && (
                  <div className="space-y-4">
                    <div className="border-2 relative border-dashed border-gray-300 rounded-lg p-4">
                      <div className="text-center">
                        <Upload className="mx-auto h-10 w-10 text-gray-400" />
                        <div className="mt-3">
                          <label className="cursor-pointer text-sm font-medium text-gray-900">
                            Upload 360° panorama
                            <input
                              type="file"
                              accept="image/*"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              onChange={(e) => handle360Upload(e.target.files)}
                              disabled={uploading360}
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">
                            Stored under /uploads/360 on Spaces
                          </p>
                        </div>
                      </div>
                    </div>
                    {uploading360 ? (
                      <div className="flex gap-2 flex-wrap">
                        {[...Array(1)].map((_, index) => (
                          <div key={index} className="animate-pulse">
                            <div className="size-[90px] rounded-lg bg-gray-100" />
                          </div>
                        ))}
                      </div>
                    ) : images360.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {images360.map((image, index) => {
                          const src = image.path?.startsWith('http')
                            ? image.path
                            : `${import.meta.env.VITE_IMAGE_URL}${image.path}`
                          return (
                            <div key={index} className="relative size-[90px] rounded-lg overflow-hidden">
                              <img
                                src={src}
                                alt={`360 view ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                                onClick={() => remove360Image(index)}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label">Brand *</label>
                <Select
                  defaultValue={delayedData.manufacturer_id}
                  {...register('manufacturer_id', { required: 'Brand is required' })}
                  error={errors.manufacturer_id?.message}
                >
                  <option value="">Select a brand</option>
                  {brandsData?.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="label">Model *</label>
                {
                  !isModelLoading &&
                  <Select
                    key={'select-model'}
                    {...register('model_group_id', { required: 'Model is required' })}
                    error={errors.model_group_id?.message}
                    disabled={!selectedBrandId}
                    defaultValue={isEdit ? modelValue : ""}
                    placeholder={(!getValues('model_group_id') && delayedData.model_group_name) ?
                      delayedData.model_group_name : 'Select an option...'}
                  >
                    <option value="">Select a model</option>
                    {modelsData?.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </Select>
                }
              </div>
              <div>
                <label className="label">Year *</label>
                <Input
                  type="number"
                  placeholder="2023"
                  {...register('year', { required: 'Year is required' })}
                  error={errors.year?.message}
                />
              </div>
              <div>
                <label className="label">Engine</label>
                <Input
                  placeholder="2.0L Turbo"
                  {...register('engine')}
                />
              </div>
              <div>
                <label className="label">Trim (Komplektasiya)</label>
                <Select {...register('trim_id')}>
                  <option value="">No trim</option>
                  {trimsData?.map((trim) => (
                    <option key={trim.id} value={trim.id}>
                      {trim.name}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-gray-500">
                  Optional. Manage the list under Filter Management → Trims.
                </p>
              </div>
              <div>
                <label className="label">Mileage</label>
                <Input
                  type="number"
                  placeholder="50000"
                  {...register('mileage')}
                />
              </div>
              <div>
                <label className="label">Price *</label>
                <Input
                  type="number"
                  placeholder="25000"
                  {...register('price', { required: 'Price is required' })}
                  error={errors.price?.message}
                />
              </div>
              <div>
                <label className="label">Seat Count</label>
                <Input
                  type="number"
                  placeholder="5"
                  min={1}
                  max={9}
                  {...register('seat_count')}
                />
              </div>
              <div>
                <label className="label">Number of Doors</label>
                <Input
                  type="number"
                  placeholder="4"
                  min={2}
                  max={6}
                  {...register('number_of_doors')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Description (Schema.org) */}
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries({
                az: 'Azərbaycan',
                ru: 'Русский',
                en: 'English',
                ko: '한국어',
                kk: 'Қазақша',
                ky: 'Кыргызча'
              }).map(([lang, label]) => (
                <div key={lang}>
                  <label className="label">
                    Description ({label})
                    {lang === currentLanguage && (
                      <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                    )}
                  </label>
                  <textarea
                    className="input min-h-[100px]"
                    placeholder={lang === 'en' ? 'Free-form vehicle description used in Schema.org JSON-LD…' : ''}
                    {...register(`description_${lang}`)}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Specifications */}
        <Card>
          <CardHeader>
            <CardTitle>Vehicle Specifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label">Fuel Type *</label>
                <Select
                  defaultValue={delayedData.fuel_type_id}
                  {...register('fuel_type_id', { required: 'Fuel type is required' })}
                  error={errors.fuel_type_id?.message}
                >
                  <option value="">Select fuel type</option>
                  {fuelTypesData?.map((fuelType) => (
                    <option key={fuelType.id} value={fuelType.id}>
                      {fuelType.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="label">Color *</label>
                <Select
                  defaultValue={delayedData.color_id}
                  {...register('color_id', { required: 'Color is required' })}
                  error={errors.color_id?.message}
                >
                  <option value="">Select color</option>
                  {colorsData?.map((color) => (
                    <option key={color.id} value={color.id}>
                      {color.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="label">Transmission *</label>
                <Select
                  defaultValue={delayedData.transmission_id}
                  {...register('transmission_id', { required: 'Transmission is required' })}
                  error={errors.transmission_id?.message}
                >
                  <option value="">Select transmission</option>
                  {transmissionsData?.map((transmission) => (
                    <option key={transmission.id} value={transmission.id}>
                      {transmission.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="label">Body Type *</label>
                <Select
                  defaultValue={delayedData.body_type_id}
                  {...register('body_type_id', { required: 'Body type is required' })}
                  error={errors.body_type_id?.message}
                >
                  <option value="">Select body type</option>
                  {bodyTypesData?.map((bodyType) => (
                    <option key={bodyType.id} value={bodyType.id}>
                      {bodyType.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="label">Vehicle Type</label>
                <Select
                  {...register('vehicle_type_id')}
                >
                  <option value="">Select vehicle type</option>
                  {vehicleTypesData?.map((vt) => (
                    <option key={vt.id} value={vt.id}>
                      {vt.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status and Delivery */}
        <Card>
          <CardHeader>
            <CardTitle>Status and Delivery</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="max-md:col-span-2">
                <label className="label">Delivery Status *</label>
                <Select
                  defaultValue={delayedData.delivery_status_id}
                  {...register('delivery_status_id', { required: 'Delivery status is required' })}
                  error={errors.delivery_status_id?.message}
                >
                  <option value="">Select delivery status</option>
                  {deliveryStatusesData?.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="max-md:col-span-2">
                <label className="label">Sale Status *</label>
                <Select
                  defaultValue={delayedData.sale_status_id}
                  {...register('sale_status_id', { required: 'Sale status is required' })}
                  error={errors.sale_status_id?.message}
                >
                  <option value="">Select sale status</option>
                  {saleStatusesData?.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </Select>
              </div>
              {/* <div>
                <label className="label">Status *</label>
                <Select
                  {...register('status', { required: 'Status is required' })}
                  error={errors.status?.message}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="sold">Sold</option>
                  <option value="reserved">Reserved</option>
                </Select>
              </div> */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Status (Multi-language)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries({
                    az: 'Azərbaycan',
                    ru: 'Русский',
                    en: 'English',
                    ko: '한국어',
                    kk: 'Қазақша',
                    ky: 'Кыргызча'
                  }).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Additional Status ({label})
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <Input
                        defaultValue={delayedData.additional_status?.[lang]}
                        placeholder={lang === 'en' ? 'Excellent condition' : ''}
                        {...register(`additional_status_${lang}`)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('sale')}
                  className="mr-2"
                />
                <label className="label">Available for Sale</label>
              </div> */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('featured')}
                  className="mr-2"
                />
                <label className="label">Featured Vehicle</label>
              </div>
              {/* <div>
                <label className="label">Manufacturer</label>
                <Select
                  {...register('manufacturer_id')}
                >
                  <option value="">Select manufacturer (optional)</option>
                  {brandsData?.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </Select>
              </div> */}
            </div>
          </CardContent>
        </Card>

        {/* Main Data Section */}
        <Card>
          <CardHeader>
            <CardTitle>Main Data</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Address (Multi-language)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries({
                    az: 'Azərbaycan',
                    ru: 'Русский',
                    en: 'English',
                    ko: '한국어',
                    kk: 'Қазақша',
                    ky: 'Кыргызча'
                  }).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Address ({label})
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <Input
                        defaultValue={isEdit ? delayedData.address?.[lang] : address?.[lang]}
                        placeholder={lang === 'en' ? '123 Main St, City, State' : ''}
                        {...register(`address_${lang}`)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Seller</label>
                  <Input
                    defaultValue='Carify LLC'
                    placeholder="John Doe"
                    {...register('seller')}
                  />
                </div>
                {/* <div>
                  <label className="label">Fem Report URL</label>
                  <Input
                    placeholder="https://www.example.com"
                    {...register('fem_report_url')}
                  />
                </div> */}
                <div>
                  <label className="label">Phone Number</label>
                  <Input
                    defaultValue="821072167665"
                    placeholder="+1 (555) 123-4567"
                    {...register('phone_number')}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* History Section */}
        <Card>
          <CardHeader>
            <CardTitle>History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label">Total Accidents</label>
                <Input
                  type="number"
                  defaultValue='0'
                  placeholder="0"
                  {...register('total_accidents')}
                />
              </div>
              <div>
                <label className="label">Plate Changes</label>
                <Input
                  type="number"
                  placeholder="0"
                  defaultValue='1'
                  {...register('plate_changes')}
                />
              </div>
              <div>
                <label className="label">Cost for My Car</label>
                <Input
                  type="number"
                  defaultValue='0'
                  placeholder="0"
                  {...register('owner_accidents')}
                />
              </div>
              {/* <div>
                <label className="label">Flood Damage Count</label>
                <Input
                  type="number"
                  placeholder="0"
                  {...register('flood_damage_count')}
                />
              </div> */}
              <div>
                <label className="label">Owner Changes</label>
                <Input
                  type="number"
                  defaultValue='1'
                  placeholder="0"
                  {...register('owner_changes')}
                />
              </div>
              {/* <div>
                <label className="label">Total Loss Count</label>
                <Input
                  type="number"
                  placeholder="0"
                  {...register('total_loss_count')}
                />
              </div> */}
              <div>
                <label className="label">Previous Usage</label>
                <Select defaultValue='personal' {...register('previous_usage')}>
                  <option value="">Select usage</option>
                  <option value="personal">Personal</option>
                  <option value="commercial">Commercial</option>
                  <option value="rental">Rental</option>
                  <option value="fleet">Fleet</option>
                </Select>
              </div>
              <div>
                <label className="label">Total accidents cost</label>
                <Input
                  defaultValue={delayedData.total_accidents_cost || 0}
                  type="number"
                  placeholder="0"
                  {...register('total_accidents_cost')}
                />
              </div>
              {/* <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('has_rental_use')}
                  className="mr-2"
                />
                <label className="label">Has Rental Use</label>
              </div> */}
            </div>
          </CardContent>
        </Card>

        {/* Technical Inspection */}
        <Card>
          <CardHeader>
            <CardTitle>Technical Inspection</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label">VIN</label>
                <Input
                  placeholder="1HGBH41JXMN109186"
                  {...register('vin')}
                />
              </div>
              {/* <div>
                <label className="label">Passed</label>
                <Input
                  placeholder="Yes/No"
                  {...register('passed')}
                />
              </div> */}
              <div className="max-md:col-span-2">
                <label className="label">Mileage Status</label>
                <Select {...register('odometer_status')}>
                  <option value="">Select status</option>
                  <option value="original">Original</option>
                  <option value="not_original">Not Original</option>
                  <option value="not_defined">Not Defined</option>
                </Select>
              </div>
              <div className="max-md:col-span-2">
                <label className="label">VIN Status</label>
                <Select {...register('vin_status')}>
                  <option value="">Select status</option>
                  <option value="verified">Verified</option>
                  <option value="not_verified">Not Verified</option>
                  <option value="invalid">Invalid</option>
                </Select>
              </div>
              <div className="max-md:col-span-2">
                <label className="label">Inspection Status</label>
                <Input
                  min={1} max={100}
                  type="number"
                  placeholder="1-100"
                  {...register('inspection_status')}
                />
              </div>
              <div className="max-md:col-span-2">
                <label className="label">Inspection Date</label>
                <Input
                  type="date"
                  {...register('inspection_date')}
                />
              </div>
              {/* <div className="md:col-span-2">
                <label className="label">Inspector Notes</label>
                <textarea
                  className="input min-h-[100px]"
                  placeholder="Technical inspection details..."
                  {...register('inspector_notes')}
                />
              </div> */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Inspector Notes (Multi-language)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries({
                    az: 'Azərbaycan',
                    ru: 'Русский',
                    en: 'English',
                    ko: '한국어',
                    kk: 'Қазақша',
                    ky: 'Кыргызча'
                  }).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Inspector Notes({label})
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <textarea
                        defaultValue={delayedData.seller_note?.[lang]}
                        className="input min-h-[100px]"
                        placeholder={lang === 'en' ? 'Additional vehicle information...' : ''}
                        {...register(`seller_note_${lang}`)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('has_repairs')}
                  className="mr-2"
                />
                <label className="label">Has Repairs</label>
              </div>
            </div>
            <div className='mt-[30px]'>
              <label className="label">Car Damage Report</label>
              <Schema setValue={setValue} getValues={getValues} />
            </div>
            <div className='mt-[30px]'>
              <label className="label">Report PDF
                {
                  getValues('report_pdf_url') && (
                    <span className="ml-2 text-xs text-primary-600 font-medium">(The file is ready: <a href={import.meta.env.VITE_IMAGE_URL + getValues('report_pdf_url')} target="_blank" rel="noopener noreferrer">Open PDF</a>)</span>
                  )
                }
              </label>
              <Input
                type="file"
                // {...register('report_pdf_url')}
                onChange={handleReportPdfUpload}
              />
            </div>
          </CardContent>
        </Card>

        {/* Warranty Section */}
        <Card>
          <CardHeader>
            <CardTitle>Warranty</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label">Warranty Expires</label>
                <Input
                  type="date"
                  {...register('warranty_expires')}
                />
              </div>
              <div>
                <label className="label">Warranty Mileage</label>
                <Input
                  type="number"
                  placeholder="100000"
                  {...register('warranty_mileage')}
                />
              </div>
              <div>
                <label className="label">Warranty Type</label>
                <Select
                  {...register('warranty_type')}
                >
                  <option value="">Select warranty type</option>
                  {warrantyTypesData?.map((warrantyType) => (
                    <option key={warrantyType.id} value={warrantyType.id}>
                      {warrantyType[`name_${currentLanguage}`] || warrantyType.name_en}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="label">Duration (Hours)</label>
                <Input
                  type="number"
                  placeholder="36"
                  {...register('duration_months')}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isWarranted')}
                  className="mr-2"
                />
                <label className="label">Is Warranted</label>
              </div>
              <div className="md:col-span-2">
                <label className="label">Warranty Notes</label>
                <Input
                  placeholder="Warranty information..."
                  {...register('warranty_notes')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Options */}
        <Card>
          <CardHeader>
            <CardTitle>Vehicle Options</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Select options available for this vehicle
              </p>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Search className="h-5 w-5 mr-2" />
                    Search Options
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-w-md">
                    <Input
                      placeholder="Search options..."
                      // value={filters.search}
                      onChange={(e) => searchOption(e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>
              {optionsCopy && optionsCopy.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[480px] overflow-auto">
                  {optionsCopy.map((option) => (
                    <div
                      key={option.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedOptions.includes(option.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => toggleOption(option.id)}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedOptions.includes(option.id)}
                          onChange={() => { }}
                          className="mt-1"
                        />
                        <div className="flex-1 flex gap-4">
                          {option.image_url && (
                            <img
                              src={`${import.meta.env.VITE_IMAGE_URL}${option.image_url}`}
                              alt={option.name}
                              className="size-10 object-cover rounded mb-2"
                            />
                          )}
                          <div>
                            <div className="font-medium text-gray-900">
                              {option.name}
                            </div>
                            {option.price > 0 && (
                              <div className="text-sm text-gray-600 mt-1">
                                ${parseFloat(option.price).toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No options available
                </div>
              )}
              {selectedOptions.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700">
                    Selected: {selectedOptions.length} option(s)
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">YouTube URLs (Multi-language)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries({
                    az: 'Azərbaycan',
                    ru: 'Русский',
                    en: 'English',
                    ko: '한국어',
                    kk: 'Қазақша',
                    ky: 'Кыргызча'
                  }).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        YouTube URLs ({label}) - one per line
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <textarea
                        defaultValue={delayedData.youtube_urls?.[lang]}
                        className="input min-h-[100px]"
                        placeholder={'https://youtube.com/watch?v=...\nhttps://youtube.com/watch?v=...'}
                        onChange={(e) => {
                          const urls = e.target.value
                          setValue(`youtube_urls_${lang}`, urls.split('\n'))
                          setValue('youtube_urls', {
                            ...getValues('youtube_urls'),
                            [lang]: urls.split('\n')
                          })
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/vehicles')}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {isEdit ? 'Update Vehicle' : 'Create Vehicle'}
          </Button>
        </div>
      </form>

      {/* Image Modal */}
      {
        showImageModal && selectedImageIndex !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 !-mt-6 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Image Settings</h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={closeImageModal}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="w-full h-48 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={
                      selectedImages[selectedImageIndex].isExisting
                        ? `${import.meta.env.VITE_IMAGE_URL}${selectedImages[selectedImageIndex].path}`
                        : URL.createObjectURL(selectedImages[selectedImageIndex].file)
                    }
                    alt="Selected image"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div>
                  <label className="label">Image Type</label>
                  <Select
                    value={selectedImages[selectedImageIndex].type}
                    onChange={(e) => updateImageType(selectedImageIndex, e.target.value)}
                  >
                    {imageTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex justify-between space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeImageModal}
                    className="flex-1"
                  >
                    Close
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={deleteImageFromModal}
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Image
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  )
}